// The agent's tool surface. Two things are structural here rather than written
// into the prompt: which tools exist at all, and which exist in which phase.
//
//   * no `write` / `replace` anywhere — both can rewrite dialogue wholesale, and
//     the review's remit is fidelity, not polish. Not registering them is the
//     enforcement; a sentence in the instructions is not.
//   * global asset writes (`merge`, asset/state edits, asset deletes) exist only
//     in the convergence phase. Per-episode work runs concurrently, and two
//     episodes merging the same duplicate actor is a real semantic conflict.
//   * `watch` carries the media budget and the 15s window cap.
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { createTool } from "@mastra/core/tools";
import { z } from "zod";
import * as sp from "@lingjingai/screenplay";
import { FINDING_INPUT, toFinding, type AuditFinding, type FindingInput } from "./finding.js";
import { grabFrame } from "./media/clip.js";
import { watchWindow, type WatchUsage } from "./media/watch.js";

const ADDR = "地址：集 `3`、场 `3-2`、单条 `3-2-5`、正式 ID（`acB4xR8p`/`scnQ7mN2v`）、资产名、或 `<资产>/<状态>`";

export interface ReviewContext {
  doc: sp.Doc;
  episode: number;                 // 0 = whole-series convergence
  phase: "episode" | "converge";
  video?: string;
  geminiKey?: string;
  mediaModel: string;
  mediaBudget: number;
  findings: AuditFinding[];
  usage: WatchUsage[];
  mediaCalls: number;
  frameDir: string;
}

export function newContext(init: Omit<ReviewContext,
  "findings" | "usage" | "mediaCalls" | "frameDir">): ReviewContext {
  return {
    ...init, findings: [], usage: [], mediaCalls: 0,
    frameDir: fs.mkdtempSync(path.join(os.tmpdir(), "review-frames-")),
  };
}

/** Everything an agent needs to start on one episode, in one call. */
function scanEpisode(doc: sp.Doc, episode: number): string {
  const addr = String(episode);
  const outline = sp.read(doc, addr, { depth: "outline" });
  const body = sp.read(doc, addr);
  const problems = sp.issues(doc, {});
  return [
    `## 场结构\n${outline}`,
    `## 正文\n${body}`,
    `## 当前校验问题（全剧）\n${problems}`,
  ].join("\n\n");
}

export function buildTools(ctx: ReviewContext): Record<string, unknown> {
  const tools: Record<string, unknown> = {};

  tools.scan = createTool({
    id: "scan",
    description: "读一集的完整上下文：场结构 + 全部正文 + 当前校验问题。开始审核一集时先调用它。",
    inputSchema: z.object({ episode: z.number().int().min(1).describe("集号，从 1 开始") }),
    execute: async ({ episode }) => scanEpisode(ctx.doc, episode),
  });

  tools.read = createTool({
    id: "read",
    description: `读任意节点。${ADDR}。不传地址返回剧本卡（题材/资产表/每集一行）。`
      + "资产地址返回人物卡（描述 + 全部状态 + 出场统计 + 未使用状态）。",
    inputSchema: z.object({
      addr: z.string().optional().describe(ADDR),
      context: z.number().int().min(0).max(20).optional().describe("单条 action 前后各带几条，默认 3"),
    }),
    execute: async ({ addr, context }) =>
      sp.read(ctx.doc, addr ?? "", context === undefined ? {} : { context }),
  });

  tools.find = createTool({
    id: "find",
    description: "按正文正则 / 说话人 / 类型定位 action，返回锚点 + 位置 + 命中窗口。",
    inputSchema: z.object({
      query: z.string().describe("正则；只按 speaker/type 过滤时传空串"),
      in: z.string().optional().describe(`限制范围，${ADDR}`),
      speaker: z.string().optional().describe("人物名或 actor id"),
      type: z.enum(["action", "dialogue", "inner_thought", "transition"]).optional(),
      limit: z.number().int().min(1).max(200).optional(),
    }),
    execute: async (input) => sp.find(ctx.doc, input.query, {
      ...(input.in ? { in: input.in } : {}),
      ...(input.speaker ? { speaker: input.speaker } : {}),
      ...(input.type ? { type: input.type } : {}),
      ...(input.limit ? { limit: input.limit } : {}),
    }),
  });

  tools.span = createTool({
    id: "span",
    description: "把一段 action 换算成集内视频时间窗口（半开区间），选 watch/frames 的窗口靠它。",
    inputSchema: z.object({ range: z.string().describe(`${ADDR}，或 \`起ID..止ID\``) }),
    execute: async ({ range }) => sp.span(ctx.doc, range),
  });

  tools.issues = createTool({
    id: "issues",
    description: "列当前校验问题，可按 severity / code 过滤。",
    inputSchema: z.object({
      severity: z.enum(["error", "warning"]).optional(),
      code: z.string().optional(),
    }),
    execute: async (input) => sp.issues(ctx.doc, input),
  });

  tools.validate = createTool({
    id: "validate",
    description: "跑整本校验。每批修改之后必须调用一次。",
    inputSchema: z.object({}),
    execute: async () => sp.validate(ctx.doc),
  });

  // ---- writes -------------------------------------------------------------

  const editFields = {
    content: z.string().optional()
      .describe("**整条覆写** action 正文：传入的字符串会成为这条 action 的全部内容，"
        + "原文被丢弃。只想改其中一个词或一段，用 `replace`，不要用这个字段。"),
    paren: z.string().nullable().optional().describe("表演提示；null 删除该字段"),
    speaker: z.string().optional().describe("人物名或 `名字·状态`；旁白/广播/画外/群声亦可"),
    ts: z.string().nullable().optional().describe("时间戳；null 删除"),
    type: z.enum(["action", "dialogue", "inner_thought", "transition"]).optional(),
    space: z.enum(["interior", "exterior"]).optional().describe("场地址：内外景"),
    time: z.enum(["day", "night", "dawn", "dusk"]).optional().describe("场地址：日夜"),
  };
  const assetFields = {
    name: z.string().optional(),
    desc: z.string().optional(),
    importance: z.enum(["featured", "background"]).optional(),
    role: z.enum(["主角", "配角"]).optional(),
  };

  tools.edit = createTool({
    id: "edit",
    description: ctx.phase === "converge"
      ? `改一个节点的字段。${ADDR}；地址决定哪些字段合法（action / 场 / 资产 / 状态）。`
      : `改一条 action 或一个场的字段。${ADDR}。资产和状态本身留给全剧收敛阶段。`,
    inputSchema: z.object({
      at: z.string().describe(ADDR),
      ...editFields,
      ...(ctx.phase === "converge" ? assetFields : {}),
    }),
    execute: async ({ at, ...fields }) => {
      const clean = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined));
      return sp.edit(ctx.doc, at, clean as never);
    },
  });

  // Fragment substitution. Without this the only way to change a word inside a
  // line was `edit`'s whole-line content field, which is how two benchmark runs
  // ended up overwriting 50-character shot descriptions with a short fragment.
  tools.replace = createTool({
    id: "replace",
    description: "把一段文本换成另一段，只动匹配到的部分，正文其余内容不变。"
      + "改人名、地名、称谓这类『一个词错了』的问题用它，不要用 edit 整条覆写。"
      + " 过滤参数与 find 相同，可用 in 限定到某集或某场。",
    inputSchema: z.object({
      pattern: z.string().describe("要被替换掉的文本（正则）"),
      to: z.string().describe("替换成什么"),
      in: z.string().optional().describe(`限制范围，${ADDR}；不传则整本`),
      speaker: z.string().optional().describe("只改某人物的台词"),
      type: z.enum(["action", "dialogue", "inner_thought", "transition"]).optional(),
      apply: z.boolean().optional()
        .describe("false 或省略只返回逐行 before/after 预览；true 才真的写入"),
    }),
    execute: async ({ pattern, to, apply, ...filters }) => sp.replace(ctx.doc, pattern, to, {
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined)),
      ...(apply ? { apply: true } : {}),
    } as never),
  });

  tools.ref = createTool({
    id: "ref",
    description: "改一个场对某资产的引用。states 是完整状态集合而非追加，`default` 也要显式写。",
    inputSchema: z.object({
      scene: z.string().describe("场地址"),
      asset: z.string().describe("人物 / 地点 / 道具的名字或 ID"),
      states: z.array(z.string()).optional().describe("完整状态集合（名字或 ID）"),
      remove: z.boolean().optional().describe("true 删除整条引用"),
    }),
    execute: async ({ scene, asset, states, remove }) =>
      sp.ref(ctx.doc, scene, asset, states ?? [], remove ?? false),
  });

  tools.insert = createTool({
    id: "insert",
    description: "在某条 action 前/后插入一条新 action，用于补原片有但剧本漏掉的内容。",
    inputSchema: z.object({
      at: z.string().describe("相邻 action 的地址"),
      where: z.enum(["before", "after"]),
      type: z.enum(["action", "dialogue", "inner_thought", "transition"]),
      content: z.string(),
      speaker: z.string().optional(),
      paren: z.string().optional(),
      ts: z.string().optional(),
    }),
    execute: async ({ at, where, type, content, speaker, paren, ts }) => {
      const out = sp.insert(ctx.doc, at, where, type, content, speaker, paren);
      if (ts) {
        const anchor = out.match(/\b(ac[1-9A-HJ-NP-Za-km-z]{6}|\d+-\d+-\d+)\b/)?.[1];
        if (anchor) sp.edit(ctx.doc, anchor, { ts });
      }
      return out;
    },
  });

  tools.move = createTool({
    id: "move",
    description: "把一条 action 挪到另一条旁边；跨场也保持它的 action_id。",
    inputSchema: z.object({
      at: z.string(), to: z.string().describe("目标邻居 action"),
      where: z.enum(["before", "after"]).optional(),
    }),
    execute: async ({ at, to, where }) => sp.move(ctx.doc, at, to, where ?? "after"),
  });

  tools.del = createTool({
    id: "del",
    description: ctx.phase === "converge"
      ? "删除节点：action / 场 / 资产 / 状态。被引用的资产或状态需要 replacement 改写或 force。"
      : "删除一条 action，或一个空场。资产和状态的删除留给全剧收敛阶段。",
    inputSchema: z.object({
      addr: z.string().describe(ADDR),
      replacement: ctx.phase === "converge"
        ? z.string().optional().describe("删状态时把引用改写到这个状态")
        : z.undefined().optional(),
      force: z.boolean().optional(),
    }),
    execute: async ({ addr, replacement, force }) => {
      if (ctx.phase !== "converge") {
        const node = sp.resolve(ctx.doc, addr);
        if (node.t === "asset" || node.t === "state") {
          throw new Error("资产与状态的删除只在全剧收敛阶段可用；本集只处理 action 与场。");
        }
      }
      return sp.del(ctx.doc, addr, {
        ...(replacement ? { replacement } : {}), ...(force ? { force } : {}),
      });
    },
  });

  if (ctx.phase === "converge") {
    tools.merge = createTool({
      id: "merge",
      description: "把一个资产并入另一个：全部场景引用与 speaker 改写过去，同名状态自动对齐。",
      inputSchema: z.object({ src: z.string(), dst: z.string() }),
      execute: async ({ src, dst }) => sp.merge(ctx.doc, src, dst),
    });
    tools.state = createTool({
      id: "state",
      description: "给已有资产加一个状态（造型档位）。",
      inputSchema: z.object({ asset: z.string(), name: z.string(), desc: z.string() }),
      execute: async ({ asset, name, desc }) => sp.state(ctx.doc, asset, name, desc),
    });
  }

  // ---- media (only with a video) ------------------------------------------

  if (ctx.video && ctx.geminiKey) {
    tools.watch = createTool({
      id: "watch",
      description: `看原片的一个短窗口（有声音），核对说话人 / 身份 / 造型 / 动作 / 资产事实。`
        + ` 单次窗口不超过 15 秒；本集预算 ${ctx.mediaBudget} 次。只在文本和结构证据不能收口时用。`,
      inputSchema: z.object({
        from: z.string().describe("窗口起点，`M:SS` 或 `H:MM:SS`"),
        to: z.string().describe("窗口终点；覆盖候选声音或动作，前后各留最多 3 秒"),
        question: z.string().describe("要核对什么。要求它只报可直接看到/听到的事实"),
      }),
      execute: async ({ from, to, question }) => {
        if (ctx.mediaCalls >= ctx.mediaBudget) {
          return `媒体核对预算已用完（${ctx.mediaBudget} 次）。证据不足的候选记为 unresolved。`;
        }
        ctx.mediaCalls += 1;
        const result = await watchWindow(ctx.video!, from, to, question, {
          apiKey: ctx.geminiKey!, model: ctx.mediaModel,
          callKey: `review:ep${ctx.episode}:watch:${ctx.mediaCalls}`,
        });
        ctx.usage.push(result.usage);
        return result.answer;
      },
    });

    tools.frames = createTool({
      id: "frames",
      description: "抽取原片若干时刻的静帧，直接进你的上下文供你自己看。没有声音，但精确且便宜。",
      inputSchema: z.object({
        timestamps: z.array(z.string()).min(1).max(6).describe("时刻列表，`M:SS`"),
        note: z.string().optional().describe("你想在这些帧里确认什么"),
      }),
      // The frames themselves reach the model through toModelOutput; the plain
      // return value is only what a transcript reader sees.
      execute: async ({ timestamps, note }) => {
        const images: { at: string; data: string }[] = [];
        const failed: string[] = [];
        for (const at of timestamps) {
          try {
            const out = path.join(ctx.frameDir, `${ctx.episode}-${at.replace(/[:.]/g, "_")}.jpg`);
            await grabFrame(ctx.video!, at, out);
            images.push({ at, data: fs.readFileSync(out).toString("base64") });
          } catch (error) {
            failed.push(`${at}: ${(error as Error).message}`);
          }
        }
        return { images, failed, note: note ?? "" };
      },
      toModelOutput: ({ images, failed, note }: any) => ({
        type: "content",
        value: [
          { type: "text", text: `原片静帧 ${images.map((i: any) => i.at).join(", ")}`
            + (note ? `（${note}）` : "") + (failed.length ? `\n抽帧失败：${failed.join("; ")}` : "") },
          ...images.map((image: any) => ({
            type: "image-data", data: image.data, mimeType: "image/jpeg",
          })),
        ],
      }),
    });
  }

  // ---- the finding ledger --------------------------------------------------

  tools.finding = createTool({
    id: "finding",
    description: "记录一条审核结论。每收口一个问题就调用一次，不要攒到最后。"
      + " outcome=fixed 表示已经改完并读回；unchanged 表示核对后无需修改；"
      + " unresolved 表示证据不足、保持原样并说明原因。",
    inputSchema: FINDING_INPUT,
    execute: async (input: FindingInput) => {
      if (input.outcome === "fixed" && input.confidence === "low") {
        return "低置信不得自动修改。要么补足证据把置信提到 medium 以上，要么把这条记为 unresolved。";
      }
      if (input.outcome === "unresolved" && !input.unresolved_reason?.trim()) {
        return "unresolved 必须写 unresolved_reason。";
      }
      const finding = toFinding(ctx.doc, input, ctx.episode, ctx.findings.length + 1);
      ctx.findings.push(finding);
      return `已记录 ${finding.id}（锚点 ${finding.anchor.episode_id ?? "?"}`
        + `${finding.anchor.action_id ? "/" + finding.anchor.action_id : ""}）。`
        + `本集已记 ${ctx.findings.length} 条。`;
    },
  });

  return tools;
}
